import { Box, Typography, useTheme } from '@mui/material';
import Fuse from 'fuse.js';
import useCharger from 'hooks/useCharger';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import LoadBalancingIcon from 'components/Icons/LoadBalancingIcon';

import { useAppSelector } from 'redux-store';
import type { PublicDatabaseDevice } from 'redux-store/slices/devices/types';

interface DevicesProps {
  chargers: PublicDatabaseDevice[];
  selectedDeviceIds: number[];
  setSelectedDeviceIds: (selectedDeviceIds: number[]) => void;
}

const ChargerGroupData: FC<{ serial: string }> = ({ serial }) => {
  const theme = useTheme();
  const loadBalancingGroupName = useCharger(serial, state => state?.log);

  if (!loadBalancingGroupName) return null;

  return (
    <RightComponentWrapper>
      <LoadBalancingIcon fontSize="small" sx={{ color: 'grey.500' }} />
      <Typography
        data-testid="device-group-names"
        variant="body1"
        noWrap
        color={theme.vars.palette.grey[500]}
      >
        {loadBalancingGroupName}
      </Typography>
    </RightComponentWrapper>
  );
};

const Devices: FC<DevicesProps> = ({
  chargers,
  selectedDeviceIds,
  setSelectedDeviceIds,
}) => {
  const { t } = useTranslation();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const devicesWithNames = useMemo(() => {
    return chargers.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [chargers, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  return (
    <Box>
      <Box mb={3} width="100%">
        <CustomSearchbar
          search={searchString}
          setSearch={setSearchString}
          hasSearched={searchString.length > 0}
          handleSearch={handleSearch}
          handleClearSearch={handleClearSearch}
        />
      </Box>
      {filteredDevices.length > 0 ? (
        filteredDevices.map(item => (
          <DeviceList
            key={item.id}
            deviceId={item.id}
            selectable
            serial={item.serial}
            selectedDeviceIds={selectedDeviceIds}
            setSelectedDeviceIds={setSelectedDeviceIds}
            rightComponent={<ChargerGroupData serial={item.serial} />}
          />
        ))
      ) : (
        <Typography variant="body1" color="grey.600" textAlign="center">
          {t('common:pages.rfids.pair_devices.no_devices_found')}
        </Typography>
      )}
    </Box>
  );
};

export default Devices;
